/**
 * BestiaryPanel — Creature library + SRD import
 *
 * Lives in GMTray Combat tab. Two modes:
 * - Library: browse saved creatures, spawn into encounter
 * - Import: search Open5e, save to bestiary
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useBestiaryStore } from '../../stores/bestiaryStore';
import { useMobStore } from '../../stores/mobStore';
import { useCampaignStore } from '../../stores/campaignStore';
import { trpc } from '../../lib/trpc';
import { Icon } from '../Icon';
import type { BestiaryEntry, Open5eMonster } from '@machina/core';

const GOLD = '#d4af37';
const SILVER_DIM = 'rgba(192, 192, 192, 0.4)';

// =============================================================================
// BESTIARY ROW
// =============================================================================

function BestiaryRow({
  entry,
  isExpanded,
  onToggle,
  onSpawn,
}: {
  entry: BestiaryEntry;
  isExpanded: boolean;
  onToggle: () => void;
  onSpawn: (count: number) => void;
}) {
  const [count, setCount] = useState(1);

  return (
    <div style={{ borderBottom: '1px solid rgba(192, 192, 192, 0.15)' }}>
      <div
        onClick={onToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 8px',
          cursor: 'pointer',
          backgroundColor: isExpanded ? 'rgba(212, 175, 55, 0.1)' : 'transparent',
        }}
        onMouseEnter={(e) => {
          if (!isExpanded) e.currentTarget.style.backgroundColor = 'rgba(192, 192, 192, 0.05)';
        }}
        onMouseLeave={(e) => {
          if (!isExpanded) e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        {/* Name + type */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 12,
            fontWeight: 500,
            color: entry.isNamed ? GOLD : '#e0e0e0',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {entry.name}
            {entry.isNamed && <span style={{ marginLeft: 4, fontSize: 9, opacity: 0.6 }}>NAMED</span>}
          </div>
          <div style={{ fontSize: 10, color: 'rgba(192, 192, 192, 0.5)' }}>
            {entry.creatureType ?? 'creature'} · {entry.size}
          </div>
        </div>

        {/* CR badge */}
        {entry.cr && (
          <span style={{
            padding: '1px 6px',
            fontSize: 10,
            fontWeight: 600,
            backgroundColor: 'rgba(212, 175, 55, 0.2)',
            color: GOLD,
            borderRadius: 3,
            flexShrink: 0,
          }}>
            CR {entry.cr}
          </span>
        )}

        {/* HP / AC */}
        <div style={{ fontSize: 10, color: 'rgba(192, 192, 192, 0.6)', flexShrink: 0, textAlign: 'right', minWidth: 55 }}>
          HP {entry.maxHp} · AC {entry.ac}
        </div>

        <Icon name={isExpanded ? 'chevron-up' : 'chevron-down'} size={12} color={SILVER_DIM} />
      </div>

      {/* Expanded details + spawn */}
      {isExpanded && (
        <div style={{
          padding: '8px 8px 10px',
          backgroundColor: 'rgba(20, 15, 30, 0.4)',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}>
          {/* Abilities */}
          <div style={{ display: 'flex', gap: 8, fontSize: 10, color: 'rgba(192, 192, 192, 0.7)' }}>
            <span>STR {entry.abilities.str}</span>
            <span>DEX {entry.abilities.dex}</span>
            <span>CON {entry.abilities.con}</span>
            <span>INT {entry.abilities.int}</span>
            <span>WIS {entry.abilities.wis}</span>
            <span>CHA {entry.abilities.cha}</span>
          </div>

          {/* Actions summary */}
          {entry.actions.length > 0 && (
            <div style={{ fontSize: 10, color: 'rgba(192, 192, 192, 0.5)' }}>
              Actions: {entry.actions.map((a) => a.name).join(', ')}
            </div>
          )}

          {/* Tags */}
          {entry.tags.length > 0 && (
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {entry.tags.map((tag) => (
                <span key={tag} style={{
                  padding: '0 5px',
                  fontSize: 9,
                  backgroundColor: 'rgba(192, 192, 192, 0.1)',
                  color: 'rgba(192, 192, 192, 0.6)',
                  borderRadius: 2,
                }}>
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Spawn controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
            <button
              onClick={(e) => { e.stopPropagation(); setCount(Math.max(1, count - 1)); }}
              style={{
                width: 22, height: 22,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundColor: 'rgba(192, 192, 192, 0.1)',
                color: '#c0c0c0',
                border: '1px solid rgba(192, 192, 192, 0.2)',
                borderRadius: 3,
                cursor: 'pointer',
                fontSize: 14,
              }}
            >-</button>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#e0e0e0', minWidth: 16, textAlign: 'center' }}>
              {count}
            </span>
            <button
              onClick={(e) => { e.stopPropagation(); setCount(Math.min(8, count + 1)); }}
              style={{
                width: 22, height: 22,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundColor: 'rgba(192, 192, 192, 0.1)',
                color: '#c0c0c0',
                border: '1px solid rgba(192, 192, 192, 0.2)',
                borderRadius: 3,
                cursor: 'pointer',
                fontSize: 14,
              }}
            >+</button>
            <button
              onClick={(e) => { e.stopPropagation(); onSpawn(count); }}
              style={{
                flex: 1,
                padding: '4px 10px',
                background: 'linear-gradient(180deg, rgba(212, 175, 55, 0.3) 0%, rgba(160, 128, 48, 0.4) 100%)',
                color: GOLD,
                border: '1px solid rgba(212, 175, 55, 0.5)',
                borderRadius: 3,
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              Spawn {count > 1 ? `${count}x` : ''}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function BestiaryPanel() {
  const entries = useBestiaryStore((s) => s.entries);
  const importFromSrd = useBestiaryStore((s) => s.importFromSrd);
  const campaignId = useCampaignStore((s) => s.currentCampaignId);

  const [mode, setMode] = useState<'library' | 'import'>('library');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [localFilter, setLocalFilter] = useState('');

  // SRD import state
  const [srdResults, setSrdResults] = useState<Open5eMonster[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [importingSlug, setImportingSlug] = useState<string | null>(null);

  // Debounced SRD search
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (mode !== 'import') return;
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (searchQuery.trim().length < 2) {
      setSrdResults([]);
      return;
    }
    searchTimerRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const result = await trpc.srd.searchMonsters.query({ search: searchQuery, limit: 10 });
        setSrdResults(result.monsters as Open5eMonster[]);
      } catch {
        setSrdResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchQuery, mode]);

  // Filter library entries locally
  const filteredEntries = localFilter
    ? entries.filter((e) =>
        e.name.toLowerCase().includes(localFilter.toLowerCase()) ||
        (e.creatureType ?? '').toLowerCase().includes(localFilter.toLowerCase()) ||
        e.tags.some((t) => t.toLowerCase().includes(localFilter.toLowerCase()))
      )
    : entries;

  // Group: Named first, then by CR
  const namedEntries = filteredEntries.filter((e) => e.isNamed);
  const regularEntries = filteredEntries.filter((e) => !e.isNamed);

  const handleSpawn = useCallback(async (entry: BestiaryEntry, count: number) => {
    if (!campaignId) return;

    const mobStore = useMobStore.getState();
    await mobStore.spawnMob({
      campaignId,
      bestiaryId: entry.id,
      name: entry.name,
      ac: entry.ac,
      maxHp: entry.maxHp,
      abilities: entry.abilities,
      size: entry.size?.toLowerCase(),
      cr: entry.cr != null ? Number(entry.cr) : undefined,
      type: entry.creatureType ?? undefined,
      imgUrl: entry.imgUrl ?? undefined,
    }, {
      count,
      rollInitiative: true,
    });
  }, [campaignId]);

  const handleImportSrd = useCallback(async (monster: Open5eMonster) => {
    if (!campaignId) return;
    setImportingSlug(monster.slug);
    try {
      await importFromSrd(campaignId, monster.slug);
    } catch (err) {
      console.error('[BestiaryPanel] Import failed:', err);
    } finally {
      setImportingSlug(null);
    }
  }, [campaignId, importFromSrd]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* Mode toggle */}
      <div style={{ display: 'flex', gap: 4 }}>
        <button
          onClick={() => setMode('library')}
          style={{
            flex: 1,
            padding: '4px 8px',
            fontSize: 11,
            fontWeight: mode === 'library' ? 600 : 400,
            backgroundColor: mode === 'library' ? 'rgba(212, 175, 55, 0.2)' : 'transparent',
            color: mode === 'library' ? GOLD : 'rgba(192, 192, 192, 0.6)',
            border: `1px solid ${mode === 'library' ? 'rgba(212, 175, 55, 0.4)' : 'rgba(192, 192, 192, 0.15)'}`,
            borderRadius: 3,
            cursor: 'pointer',
          }}
        >
          Library ({entries.length})
        </button>
        <button
          onClick={() => setMode('import')}
          style={{
            flex: 1,
            padding: '4px 8px',
            fontSize: 11,
            fontWeight: mode === 'import' ? 600 : 400,
            backgroundColor: mode === 'import' ? 'rgba(212, 175, 55, 0.2)' : 'transparent',
            color: mode === 'import' ? GOLD : 'rgba(192, 192, 192, 0.6)',
            border: `1px solid ${mode === 'import' ? 'rgba(212, 175, 55, 0.4)' : 'rgba(192, 192, 192, 0.15)'}`,
            borderRadius: 3,
            cursor: 'pointer',
          }}
        >
          + Import SRD
        </button>
      </div>

      {/* Library mode */}
      {mode === 'library' && (
        <>
          {/* Filter */}
          <input
            type="text"
            value={localFilter}
            onChange={(e) => setLocalFilter(e.target.value)}
            placeholder="Filter creatures..."
            style={{
              padding: '4px 8px',
              fontSize: 11,
              backgroundColor: 'rgba(20, 15, 30, 0.6)',
              color: '#e0e0e0',
              border: '1px solid rgba(192, 192, 192, 0.15)',
              borderRadius: 3,
              outline: 'none',
            }}
          />

          {/* List */}
          <div style={{
            maxHeight: 280,
            overflowY: 'auto',
            border: '1px solid rgba(192, 192, 192, 0.1)',
            borderRadius: 3,
          }}>
            {/* Named creatures */}
            {namedEntries.length > 0 && (
              <>
                <div style={{
                  padding: '3px 8px',
                  fontSize: 9,
                  fontWeight: 600,
                  color: GOLD,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  backgroundColor: 'rgba(212, 175, 55, 0.05)',
                }}>
                  Named Creatures
                </div>
                {namedEntries.map((entry) => (
                  <BestiaryRow
                    key={entry.id}
                    entry={entry}
                    isExpanded={expandedId === entry.id}
                    onToggle={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                    onSpawn={(count) => handleSpawn(entry, count)}
                  />
                ))}
              </>
            )}

            {/* Regular creatures */}
            {regularEntries.length > 0 && (
              <>
                {namedEntries.length > 0 && (
                  <div style={{
                    padding: '3px 8px',
                    fontSize: 9,
                    fontWeight: 600,
                    color: 'rgba(192, 192, 192, 0.5)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    backgroundColor: 'rgba(192, 192, 192, 0.03)',
                  }}>
                    Creatures
                  </div>
                )}
                {regularEntries.map((entry) => (
                  <BestiaryRow
                    key={entry.id}
                    entry={entry}
                    isExpanded={expandedId === entry.id}
                    onToggle={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                    onSpawn={(count) => handleSpawn(entry, count)}
                  />
                ))}
              </>
            )}

            {filteredEntries.length === 0 && (
              <div style={{
                padding: 16,
                textAlign: 'center',
                color: 'rgba(192, 192, 192, 0.4)',
                fontSize: 11,
                fontStyle: 'italic',
              }}>
                {entries.length === 0 ? 'Bestiary is empty. Import creatures from the SRD.' : 'No matches.'}
              </div>
            )}
          </div>
        </>
      )}

      {/* Import mode */}
      {mode === 'import' && (
        <>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search Open5e monsters..."
              style={{
                width: '100%',
                padding: '4px 8px',
                paddingRight: 28,
                fontSize: 11,
                backgroundColor: 'rgba(20, 15, 30, 0.6)',
                color: '#e0e0e0',
                border: '1px solid rgba(192, 192, 192, 0.15)',
                borderRadius: 3,
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
            {isSearching && (
              <span style={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: 10,
                color: 'rgba(192, 192, 192, 0.4)',
              }}>...</span>
            )}
          </div>

          {/* SRD Results */}
          <div style={{
            maxHeight: 280,
            overflowY: 'auto',
            border: '1px solid rgba(192, 192, 192, 0.1)',
            borderRadius: 3,
          }}>
            {srdResults.map((monster) => {
              const alreadyImported = entries.some((e) => e.slug === monster.slug);
              const isImporting = importingSlug === monster.slug;

              return (
                <div
                  key={monster.slug}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '5px 8px',
                    borderBottom: '1px solid rgba(192, 192, 192, 0.1)',
                    fontSize: 11,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, color: '#e0e0e0' }}>{monster.name}</div>
                    <div style={{ fontSize: 10, color: 'rgba(192, 192, 192, 0.5)' }}>
                      CR {monster.challenge_rating} · {monster.type} · HP {monster.hit_points}
                    </div>
                  </div>
                  <button
                    onClick={() => handleImportSrd(monster)}
                    disabled={alreadyImported || isImporting}
                    style={{
                      padding: '2px 8px',
                      fontSize: 10,
                      fontWeight: 600,
                      backgroundColor: alreadyImported
                        ? 'rgba(192, 192, 192, 0.1)'
                        : 'rgba(212, 175, 55, 0.2)',
                      color: alreadyImported ? 'rgba(192, 192, 192, 0.4)' : GOLD,
                      border: `1px solid ${alreadyImported ? 'rgba(192, 192, 192, 0.1)' : 'rgba(212, 175, 55, 0.3)'}`,
                      borderRadius: 3,
                      cursor: alreadyImported ? 'default' : 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {isImporting ? '...' : alreadyImported ? 'Saved' : '+ Save'}
                  </button>
                </div>
              );
            })}

            {srdResults.length === 0 && searchQuery.length >= 2 && !isSearching && (
              <div style={{
                padding: 12,
                textAlign: 'center',
                color: 'rgba(192, 192, 192, 0.4)',
                fontSize: 11,
              }}>
                No results
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
